import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {StateType} from "../../store/store"
import {getDashboardLogs, getDashboardProblems, setDashboardFilter, setDashboardLogs, setDashboardProblems} from "../../store/appReducer"
import {Loading} from "../../components/Loading/Loading"
import {Helmet} from "react-helmet-async"
import {useHistory, useLocation, NavLink} from "react-router-dom"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {center} from "../../helps/tableHelper"
import {Card, CheckboxProps, Col, Collapse, CollapseProps, DatePicker, FloatButton, Modal, Row, Skeleton, Table, Tag} from "antd"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {filterToString} from "../../helps/filterToString"
import moment from "moment/moment"
import {FormSelect} from "../../components/Form/FormSelect"
import {limitOptionsSelect} from "../../helps/optionsSelect"
import {getClassNameLocalLogRowForTable} from "../../helps/getClassNameLocalLogRowForTable"
import {setWSPSLeakDeviceLocalLog, solveArrayPSLeakDeviceLocalLogs} from "../../store/psLeakDeviceLocalLogReducer"
import {useFormik} from "formik"
import {FormTextarea} from "../../components/Form/FormTextarea"
import * as yup from "yup"
import {BsJournalCheck} from "react-icons/bs"
import {useGetCompanies, useGetDevices, useGetObjectGroups, useGetObjects} from "../../hooks/useFetchHooks"
import classNames from "classnames"
import s from "./dashboard.module.scss"
import {setWSPSLeakDevice} from "../../store/psLeakDeviceReducer"
import {setWSPSLeakDeviceIndication} from "../../store/psLeakDeviceIndicationReducer"
import {getTimeZoneDiffString} from "../../helps/timezoneDiff"
import {UserTypeEnum} from "../../types/types"
import {FormInput} from "../../components/Form/FormInput"
import locale from "antd/es/date-picker/locale/ru_RU"
import dayjs from "dayjs"

const schema = yup.object().shape({
    comment: yup.string().min(20, "Минимальная длина 20 символов").required("Введите комментарий"),
})

const Dashboard = () =>
{
    const dispatch = useDispatch()
    const location = useLocation()
    const history = useHistory()
    const status = useSelector((state: StateType) => state.appReducer.status)
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const dashboardProblems = useSelector((state: StateType) => state.appReducer.dashboardProblems)
    const dashboardLogs = useSelector((state: StateType) => state.appReducer.dashboardLogs)
    const filter = useSelector((state: StateType) => state.appReducer.filter)
    const wsDevices = useSelector((state: StateType) => state.psLeakDeviceReducer.ws)
    const wsLogs = useSelector((state: StateType) => state.psLeakDeviceLocalLogReducer.ws)
    const wsIndications = useSelector((state: StateType) => state.psLeakDeviceIndicationReducer.ws)
    const [tableSize, setTableSize] = useState(0)
    const ref = useRef<HTMLDivElement>(null)
    const controller = useMemo(() => new AbortController(), [])
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([])
    const [loading, setLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)

    const getCompanyParams = useMemo(() => ({excludeSystem: true}), [])
    const companies = useGetCompanies(getCompanyParams)

    const objectGroupsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
    }), [filter.idCompany])
    const objectGroups = useGetObjectGroups(objectGroupsQuery)

    const objectsQuery = useMemo(() => ({
        idCompany: filter.idCompany,
        idObjectGroup: filter.idObjectGroup,
    }), [filter.idCompany, filter.idObjectGroup])
    const objects = useGetObjects(objectsQuery)

    const devicesQuery = useMemo(() => ({
        idCompany: filter.idCompany,
        idObjectGroup: filter.idObjectGroup,
        idObject: filter.idObject,
    }), [filter.idCompany, filter.idObjectGroup, filter.idObject])
    const devices = useGetDevices(devicesQuery)

    const formik = useFormik({
        initialValues: {
            comment: "",
        },
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (values) =>
        {
            try
            {
                setLoading(true)
                const {payload}: any = await dispatch(solveArrayPSLeakDeviceLocalLogs({
                    ids: selectedRowKeys,
                    comment: values.comment,
                    controller,
                }))
                if (payload)
                {
                    setSelectedRowKeys([])
                    setShowModal(false)
                    resetForm()
                }
            } finally
            {
                setLoading(false)
            }
        },
    })
    const {values, handleChange, handleBlur, handleSubmit, errors, touched, setFieldValue, submitForm, resetForm} = formik

    const columns = useMemo(() =>
    {
        const cols = []

        cols.push(
            {
                title: "Локальный ID",
                dataIndex: "idLocal",
                key: "idLocal",
                align: center,
            },
            {
                title: "Дата",
                dataIndex: "date",
                key: "date",
                align: center,
            },
        )

        if (profile?.idCompany === ID_SYSTEM_COMPANY) cols.push({
            title: "Компания",
            dataIndex: "nameCompany",
            key: "nameCompany",
            align: center,
        })

        cols.push(
            {
                title: "Группа",
                dataIndex: "nameObjectGroup",
                key: "nameObjectGroup",
                align: center,
            },
            {
                title: "Объект",
                dataIndex: "nameObject",
                key: "nameObject",
                align: center,
            },
            {
                title: "Оборудование",
                dataIndex: "nameDevice",
                key: "nameDevice",
                align: center,
            },
            {
                title: "Тип",
                dataIndex: "nameLogType",
                key: "nameLogType",
                align: center,
            },
            {
                title: "Описание",
                dataIndex: "description",
                key: "description",
            },
            {
                title: "Статус",
                dataIndex: "solved",
                key: "solved",
                render: (solved: boolean | null) =>
                {
                    if (typeof solved === "boolean") return (
                        <Tag color={solved ? "green" : "volcano"} style={{marginRight: 0}}>{solved ? "Решено" : "Не решено"}</Tag>
                    )
                    return <Tag color="default" style={{marginRight: 0}}>Системное</Tag>
                },
                align: center,
            },
        )

        return cols
    }, [profile])

    useEffect(() =>
    {
        if (status !== "loading") setTableSize(0)
    }, [status])

    useEffect(() =>
    {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter))
        {
            dispatch(setDashboardFilter({...filterString}))
        }
        else if (location.search !== filterToString(filter))
        {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    const getDashboardProblemsFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        dispatch(getDashboardProblems({
            withoutLoadingIndicator,
            controller,
        }))
    }, [dispatch, controller])

    const getDashboardLogsFunc = useCallback((withoutLoadingIndicator = false) =>
    {
        if (location?.search)
        {
            dispatch(getDashboardLogs({
                ...filterFromString(location.search),
                withoutLoadingIndicator,
                status: "0",
                controller,
            }))
        }
    }, [dispatch, location, controller])

    useEffect(() =>
    {
        getDashboardProblemsFunc()

        return () =>
        {
            dispatch(setDashboardProblems(null))
        }
    }, [dispatch, getDashboardProblemsFunc])

    useEffect(() =>
    {
        getDashboardLogsFunc()

        return () =>
        {
            dispatch(setDashboardLogs(null))
        }
    }, [dispatch, getDashboardLogsFunc])

    useEffect(() =>
    {
        return () =>
        {
            controller.abort()
        }
    }, [dispatch, controller])

    useEffect(() =>
    {
        if (profile?.idCompany !== ID_SYSTEM_COMPANY && filter?.idCompany !== profile?.idCompany)
        {
            history.replace({search: filterToString({...filter, idCompany: profile?.idCompany})})
        }
    }, [profile])

    useEffect(() =>
    {
        if (wsDevices)
        {
            getDashboardProblemsFunc(true)
        }
        dispatch(setWSPSLeakDevice(null))
    }, [dispatch, wsDevices, getDashboardProblemsFunc])

    useEffect(() =>
    {
        if (wsLogs)
        {
            getDashboardLogsFunc(true)
            getDashboardProblemsFunc(true)
            setSelectedRowKeys([])
        }
        dispatch(setWSPSLeakDeviceLocalLog(null))
    }, [dispatch, wsLogs, getDashboardLogsFunc, getDashboardProblemsFunc])

    useEffect(() =>
    {
        if (wsIndications)
        {
            getDashboardProblemsFunc(true)
        }
        dispatch(setWSPSLeakDeviceIndication(null))
    }, [dispatch, wsIndications, getDashboardProblemsFunc])

    const dashboardProblemsMap: CollapseProps["items"] = useMemo(() => dashboardProblems?.map(g => ({
            key: `${g.idObjectGroup}`,
            label: `${g.nameObjectGroup}${profile?.idCompany === ID_SYSTEM_COMPANY ? ` - ${g.nameCompany}` : ""}`,
            children: <div className={s.problemGroupContainer}>
                {g.objects?.map(o => (
                    <NavLink key={o.idObject}
                             to={`/object/${o.idObject}`}
                             className={classNames(s.problemObjectContainer)}>
                        <div>{o.nameObject}</div>
                        <div className={s.problemsContainer}>
                            {o.problems?.map(d => (
                                <div key={d.message}
                                     style={{color: d.type === "Danger" ? "#d48806" : "#d4380d"}}>
                                    {d.message}
                                </div>
                            ))}
                        </div>
                    </NavLink>
                ))}
            </div>,
        }
    )), [dashboardProblems, profile])

    const dataLogs = useMemo(() => dashboardLogs?.data?.map(i =>
    {
        return {
            key: i.id,
            id: i.id,
            idLocal: i.idLocal,
            idLogType: i.idLogType,
            nameLogType: i.nameLogType,
            nameDevice: i.nameDevice,
            nameObject: i.nameObject,
            nameObjectGroup: i.nameObjectGroup,
            nameCompany: i.nameCompany,
            description: i.description,
            date: `${moment(i.date).format("DD.MM.YYYY HH:mm")} ${getTimeZoneDiffString(i.timeZone)}`,
            solved: i.solved,
        }
    }), [dashboardLogs])

    const rowSelection = useMemo(() => ({
        selectedRowKeys,
        onChange: (newSelectedRowKeys: Array<any>) => setSelectedRowKeys(newSelectedRowKeys),
        getCheckboxProps: (i: NonNullable<typeof dataLogs>[number]): Partial<Omit<CheckboxProps, "checked" | "defaultChecked">> =>
            (profile?.idUserType !== UserTypeEnum.OPERATOR_COMPANY && profile?.idUserType !== UserTypeEnum.BUH_COMPANY) || (i.idLogType < 1 || i.idLogType > 10) ? {disabled: false} : {disabled: true},
    }), [selectedRowKeys, profile])

    const companyOptions = useMemo(() => companies?.data?.map(i => ({label: i.name, value: i.id})), [companies])
    const objectGroupsOptions = useMemo(() => objectGroups?.data?.map(i => ({label: i.name, value: i.id})), [objectGroups])
    const objectOptions = useMemo(() => objects?.data?.map(i => ({label: i.name, value: i.id})), [objects])
    const deviceOptions = useMemo(() => devices?.data?.map(i => ({label: i.name, value: i.id})), [devices])

    return (
        <div className="tabled">
            <Helmet>
                <title>Логи устройств</title>
            </Helmet>
            <Row gutter={[24, 0]}>
                <Col xs={24}>
                    {
                        dashboardProblemsMap
                            ? <Collapse items={dashboardProblemsMap}
                                        style={{marginBottom: 20}}
                                        defaultActiveKey={[...dashboardProblemsMap.map(i => i.key?.toString() ?? "")]}/>
                            : <Skeleton/>
                    }

                    <Card variant="borderless"
                          className="criclebox tablespace mb-24"
                          title="События"
                          extra={
                              <div className="table-title-extra">
                                  <FormInput placeholder="Поиск"
                                             containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                             value={filter?.search}
                                             name="search"
                                             onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                             allowClear
                                  />

                                  <DatePicker locale={locale}
                                              showTime
                                              placeholder="Дата от"
                                              format="DD.MM.YYYY HH:mm"
                                              showSecond={false}
                                              style={{height: 50, width: 150}}
                                              value={filter.dateFrom ? dayjs(filter.dateFrom) : null}
                                              onChange={(value) => history.replace({search: filterToString({...filter, dateFrom: value ? value.format("YYYY-MM-DD HH:mm") : null, offset: 0})})}
                                              disabledDate={(currentDate) => currentDate > dayjs(new Date())}
                                  />

                                  <DatePicker locale={locale}
                                              showTime
                                              placeholder="Дата по"
                                              format="DD.MM.YYYY HH:mm"
                                              showSecond={false}
                                              style={{height: 50, width: 150}}
                                              value={filter.dateTo ? dayjs(filter.dateTo) : null}
                                              onChange={(value) => history.replace({search: filterToString({...filter, dateTo: value ? value.format("YYYY-MM-DD HH:mm") : null, offset: 0})})}
                                              disabledDate={(currentDate) => filter.dateFrom ? currentDate < dayjs(filter.dateFrom) || currentDate > dayjs(new Date()) : currentDate > dayjs(
                                                  new Date())}
                                  />

                                  {
                                      profile?.idCompany === ID_SYSTEM_COMPANY
                                          ?
                                          <FormSelect value={filter?.idCompany}
                                                      containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                      dropdownStyle={{minWidth: "min-content"}}
                                                      onChange={(value) =>
                                                      {
                                                          setTableSize(ref?.current?.offsetHeight || 0)
                                                          history.replace({
                                                              search: filterToString({
                                                                  ...filter,
                                                                  idCompany: value,
                                                                  offset: 0,
                                                                  idObjectGroup: "",
                                                                  idObject: "",
                                                                  idDevice: "",
                                                              }),
                                                          })
                                                      }}
                                                      options={companyOptions}
                                                      placeholder="Компания"
                                                      allowClear
                                                      placement="bottomRight"
                                          />
                                          : null
                                  }

                                  <FormSelect value={filter?.idObjectGroup}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) =>
                                              {
                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                  history.replace({
                                                      search: filterToString({
                                                          ...filter,
                                                          idObjectGroup: value,
                                                          offset: 0,
                                                          idObject: "",
                                                          idDevice: "",
                                                      }),
                                                  })
                                              }}
                                              options={objectGroupsOptions}
                                              placeholder="Группа"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.idObject}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) =>
                                              {
                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                  history.replace({
                                                      search: filterToString({
                                                          ...filter,
                                                          idObject: value,
                                                          offset: 0,
                                                          idDevice: "",
                                                      }),
                                                  })
                                              }}
                                              options={objectOptions}
                                              placeholder="Объект"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.idDevice}
                                              containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) =>
                                              {
                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                  history.replace({
                                                      search: filterToString({
                                                          ...filter,
                                                          idDevice: value,
                                                          offset: 0,
                                                      }),
                                                  })
                                              }}
                                              options={deviceOptions}
                                              placeholder="Оборудование"
                                              allowClear
                                              placement="bottomRight"
                                  />

                                  <FormSelect value={filter?.limit}
                                              containerStyle={{marginBottom: 0, width: "auto"}}
                                              dropdownStyle={{minWidth: "min-content"}}
                                              onChange={(value) =>
                                              {
                                                  setTableSize(ref?.current?.offsetHeight || 0)
                                                  history.replace({search: filterToString({...filter, limit: value, offset: 0})})
                                              }}
                                              options={limitOptionsSelect}
                                              placement="bottomRight"
                                  />
                              </div>
                          }
                    >
                        <div className="table-responsive" ref={ref} style={{minHeight: tableSize}}>
                            {
                                dashboardLogs
                                    ? <Table columns={columns}
                                             dataSource={dataLogs}
                                             pagination={filter?.limit ? {
                                                 position: dashboardLogs?.data?.length >= 5 ? ["bottomLeft", "topLeft"] : ["bottomLeft"],
                                                 pageSize: filter.limit,
                                                 hideOnSinglePage: true,
                                                 total: dashboardLogs.total,
                                                 current: filter?.offset / filter?.limit + 1,
                                                 onChange: (page, pageSize) =>
                                                 {
                                                     setTableSize(ref?.current?.offsetHeight || 0)
                                                     const obj = {
                                                         limit: pageSize,
                                                         offset: (page - 1) * pageSize,
                                                     }
                                                     history.replace({search: filterToString({...filter, ...obj})})
                                                 },
                                                 showSizeChanger: false,
                                             } : false}
                                             className="ant-border-space"
                                             onRow={({id}) => ({
                                                 onClick: (e) => history.push(`/psLeakDeviceLocalLog/${id}`),
                                                 onMouseDown: (e) => e.button === 1 && window.open(`/psLeakDeviceLocalLog/${id}`, "_blank", "noopener,noreferrer"),
                                             })}
                                             rowClassName={(i) => !i.solved ? getClassNameLocalLogRowForTable(i.idLogType) : ""}
                                             rowSelection={rowSelection}
                                    />
                                    : <Loading/>
                            }
                        </div>
                    </Card>
                </Col>
            </Row>

            <Modal title="Отметить решенными"
                   centered
                   open={showModal}
                   onOk={submitForm}
                   onCancel={() =>
                   {
                       setShowModal(false)
                       resetForm()
                   }}
                   okText="Сохранить"
                   okButtonProps={{loading}}
            >
                <FormTextarea placeholder="Комментарий"
                              value={values.comment}
                              name="comment"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={touched.comment && errors.comment}
                              allowClear
                              autoSize={{minRows: 2}}
                />
            </Modal>

            {
                selectedRowKeys?.length > 0
                    ? <FloatButton onClick={() => setShowModal(true)}
                                   tooltip={<div>Отметить решенными</div>}
                                   type="primary"
                                   icon={<BsJournalCheck/>}
                    />
                    : null
            }
        </div>
    )
}

export default Dashboard